import {
	StyledEngineProvider,
	ThemeProvider,
	createTheme,
} from '@mui/material/styles';

import React from 'react';

import { Provider } from 'react-redux';

import { BrowserRouter } from 'react-router-dom';

import AuthProvider from 'context/AuthProvider';
import DataDogProvider from 'context/DataDogProvider';
import FeatureFlagsProvider from 'context/FeatureFlagWrapper';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import ReactDOM from 'react-dom/client';

import { APP_THEME } from 'common/constants';
import localStorageWrapper from 'common/loginUtils/local-storage-wrapper';

import App from './App';
import './common/assets/styles/common.scss';
import './common/i18n';
import { store } from './common/store/AppStore';

import styles from './index.module.scss';

const { fontFamilyHelvetica, primaryOrange, primaryBlue } = styles;

/**
 * Retrieves the app theme from local storage, or defaults to 'theme-blue' if not found.
 * @returns {string} The app theme.
 */
const appTheme =
	localStorageWrapper.getStorageItem('appTheme') ?? APP_THEME.BLUE;

/**
 * Selects the root element in the document.
 * @returns {Element | null} The selected root element, or null if not found.
 */
const rootElement = document.querySelector('body');
rootElement?.setAttribute('app-theme', appTheme);

/**
 * Creates a theme object with custom typography and palette settings.
 *
 * @returns The created theme object.
 */
const theme = createTheme({
	typography: {
		fontFamily: fontFamilyHelvetica,
	},
	palette: {
		primary: {
			main: appTheme === APP_THEME.ORANGE ? primaryOrange : primaryBlue,
		},
	},
});

const initializeAsynchronously = (
	clientSideID: string,
	root: ReactDOM.Root,
) => {
	asyncWithLDProvider({
		clientSideID,
		context: {
			kind: process.env.REACT_APP_LD_CONTEXT_KIND,
			key: process.env.REACT_APP_LD_CONTEXT_KEY,
			name: process.env.REACT_APP_LD_CONTEXT_NAME,
			email: process.env.REACT_APP_LD_CONTEXT_EMAIL,
		},
	}).then((LDProvider) =>
		root.render(
			<React.StrictMode>
				<StyledEngineProvider injectFirst>
					<Provider store={store}>
						<BrowserRouter>
							<AuthProvider>
								<FeatureFlagsProvider>
									<LDProvider>
										<DataDogProvider>
											<ThemeProvider theme={theme}>
												<App />
											</ThemeProvider>
										</DataDogProvider>
									</LDProvider>
								</FeatureFlagsProvider>
							</AuthProvider>
						</BrowserRouter>
					</Provider>
				</StyledEngineProvider>
			</React.StrictMode>,
		),
	);
};

(async () => {
	const clientSideID =
		process.env.REACT_APP_LD_CLIENT_SIDE_ID ?? '65f09dfbbc1f5c10608e20b6';
	const root = ReactDOM.createRoot(
		document.getElementById('root') as HTMLElement,
	);

	initializeAsynchronously(clientSideID, root);
})();
